import React, { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { User } from '../../types/user';
import { useTheme } from '../../contexts/ThemeContext';
import { formatProfilePictureUrl } from '../../utils/imageUtils';

interface OrgChartNodeData {
    user: User;
    isRoot?: boolean;
}

const OrgChartNode = ({ data }: NodeProps<OrgChartNodeData>) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { user, isRoot } = data;
    const [imageError, setImageError] = useState(false);

    // Reset image error state when user changes
    useEffect(() => {
        setImageError(false);
    }, [user.id, user.pfp_url]);

    return (
        <div className={`
            relative flex flex-col items-center p-4 rounded-xl border shadow-sm transition-all duration-300 hover:shadow-lg group
            ${isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-indigo-200'}
            min-w-[180px]
        `}>
            {/* Input Handle (Top) - hidden for root */}
            {!isRoot && (
                <Handle
                    type="target"
                    position={Position.Top}
                    className={`!w-2 !h-2 !bg-gray-400 ${isDark ? '!border-gray-800' : '!border-white'}`}
                />
            )}

            {/* Email Tooltip */}
            <div className={`
                absolute -top-10 left-1/2 transform -translate-x-1/2 px-3 py-1.5 rounded-md text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20 shadow-md
                ${isDark ? 'bg-gray-700 text-white border border-gray-600' : 'bg-gray-900 text-white'}
            `}>
                {user.email}
                {/* Arrow */}
                <div className={`
                    absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45
                    ${isDark ? 'bg-gray-700 border-r border-b border-gray-600' : 'bg-gray-900'}
                `}></div>
            </div>

            <div className={`
                h-14 w-14 rounded-full overflow-hidden mb-3 ring-2 ring-offset-2 transition-all duration-300
                ${isDark ? 'ring-indigo-500 ring-offset-gray-800' : 'ring-indigo-100 ring-offset-white group-hover:ring-indigo-500'}
            `}>
                {user.pfp_url && !imageError ? (
                    <img
                        src={formatProfilePictureUrl(user.pfp_url)}
                        alt={user.full_name}
                        className="h-full w-full object-cover"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className={`h-full w-full flex items-center justify-center font-bold text-xl ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-indigo-50 text-indigo-600'}`}>
                        {user.full_name.charAt(0).toUpperCase()}
                    </div>
                )}
            </div>

            <div className={`font-semibold text-sm mb-0.5 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {user.full_name}
            </div>

            <div className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                {user.role}
            </div>

            {/* Output Handle (Bottom) */}
            <Handle
                type="source"
                position={Position.Bottom}
                className={`!w-2 !h-2 !bg-gray-400 ${isDark ? '!border-gray-800' : '!border-white'}`}
            />
        </div>
    );
};

export default memo(OrgChartNode);
